/* eslint-disable react/require-default-props */
import React, { FC, useEffect, useState } from 'react'
import { inject } from 'mobx-react'
import { FaCheckCircle, FaRegCircle } from 'react-icons/fa'
import { Button as BootBtn } from 'react-bootstrap'
import API from '@aws-amplify/api'
import { ClickAwayListener, Grow, Paper, Popper, Divider, ListItemText } from '@material-ui/core'
import { Prospects as ProspectsStore } from '../../../../stores/prospects'
import { Search as SearchStore } from '../../../../stores/search'
import Spinner from '../../../Spinner'
import { ToastTypeEnum } from '../../Toasts'
import './styles.scss'

interface Props {
  prospects?: ProspectsStore
  search?: SearchStore
  openDeletePopper: boolean
  anchorRef: React.RefObject<HTMLButtonElement>
  setOpenDeletePopper: (value: boolean) => void
}

const DeletePopper: FC<Props> = ({
  prospects,
  search,
  anchorRef,
  openDeletePopper,
  setOpenDeletePopper,
}) => {
  const [deleteType, setDeleteType] = useState('')

  const handleCloseDeletePopper = (event?: React.MouseEvent<EventTarget>): void => {
    if (anchorRef.current && anchorRef.current.contains(event?.target as HTMLElement)) {
      return
    }

    setOpenDeletePopper(false)
  }
  const prevOpen = React.useRef(openDeletePopper)
  useEffect(() => {
    if (prevOpen.current === true && openDeletePopper === false) {
      anchorRef?.current?.focus()
    }

    prevOpen.current = openDeletePopper
  }, [openDeletePopper])

  const handleDeleteTypeChange = (value): void => {
    if (deleteType === value) {
      setDeleteType('')
    } else {
      setDeleteType(value)
    }
    if (value === 'Negative') {
      setOpenDeletePopper(false)
    }
  }

  if (prospects?.isLoading) {
    return <Spinner />
  }
  if (!prospects?.prospect) {
    return null
  }

  let localDomain = prospects?.prospect.domain
  if (!localDomain) {
    localDomain = prospects?.prospect.email?.substring(
      prospects?.prospect.email.lastIndexOf('@') + 1,
    )
  }
  const { name, email } = prospects.prospect

  const toDelete = async (): Promise<void> => {
    try {
      await API.del('mav-outlook-api', `/leads/${email}`, {})

      prospects?.setCacheValid(false)
      prospects?.setDeleteLeadConfirmationOpen(false)
      search?.setCacheValid(false)
      search?.setProspects(search?.prospects.filter(prospect => prospect.email !== email))
      setOpenDeletePopper(false)
      search?.pushToToasts({
        type: ToastTypeEnum.INFO,
        message: 'Lead deleted',
      })
    } catch (e) {
      console.error(e)

      prospects?.setIsLoading(false)
    }
  }
  return (
    <Popper
      open={openDeletePopper}
      anchorEl={anchorRef.current}
      role={undefined}
      transition
      disablePortal
      style={{
        zIndex: 1,
      }}
    >
      {({ TransitionProps, placement }): JSX.Element => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
          }}
        >
          <Paper style={{ maxWidth: 400, float: 'right' }}>
            <ClickAwayListener onClickAway={handleCloseDeletePopper}>
              <div className="delete-form">
                <ListItemText>{`Are you sure you want to delete ${name}?`}</ListItemText>
                <div className="delete-type-container">
                  <Divider />
                  <div className="delete-type">
                    <div
                      role="button"
                      tabIndex={0}
                      onKeyDown={(): void => {}}
                      className="delete-type-item"
                      onClick={(): void => handleDeleteTypeChange('Affirmative')}
                    >
                      {deleteType === 'Affirmative' ? (
                        <FaCheckCircle className="check_icon" />
                      ) : (
                        <FaRegCircle />
                      )}
                      Affirmative
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      onKeyDown={(): void => {}}
                      className="delete-type-item"
                      onClick={(): void => handleDeleteTypeChange('Negative')}
                    >
                      {deleteType === 'Negative' ? (
                        <FaCheckCircle className="check_icon" />
                      ) : (
                        <FaRegCircle />
                      )}
                      Negative
                    </div>
                  </div>
                  <Divider />
                </div>
                {deleteType === 'Affirmative' && (
                  <BootBtn variant="danger" block onClick={(): Promise<void> => toDelete()}>
                    DELETE FROM MY HANGAR
                  </BootBtn>
                )}
              </div>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  )
}

export default inject('prospects', 'related', 'search', 'companyInfo')(DeletePopper)
